import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Slider } from '../components/ui/slider';
import { useAuth } from '../context/AuthContext';
import { Logo } from '../components/Logo';
import { 
  Leaf, ArrowLeft, Calendar, Pill, Check, Loader2, 
  TrendingUp, TrendingDown, Minus, ChevronLeft, ChevronRight,
  Sparkles, Award, AlertCircle, BookOpen, BarChart3, Flame
} from 'lucide-react';
import { toast } from 'sonner';
import { formatLocalDate, todayLocal, daysAgoLocal } from '../utils/dateHelpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SupplementJournalPage() {
  const navigate = useNavigate();
  const { user, getAuthHeader, loading: authLoading } = useAuth();
  
  const [currentDate, setCurrentDate] = useState(todayLocal());
  const [supplements, setSupplements] = useState([]);
  const [entries, setEntries] = useState([]);
  const [overallFeeling, setOverallFeeling] = useState(5);
  const [notes, setNotes] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('log'); // 'log', 'insights', 'stats'
  const [correlations, setCorrelations] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth?redirect=/journal');
    }
  }, [user, authLoading, navigate]);

  const fetchData = useCallback(async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      const [journalRes, assessRes] = await Promise.all([
        axios.get(`${API}/journal/${currentDate}`, { headers: getAuthHeader() }),
        axios.get(`${API}/assessments`, { headers: getAuthHeader() })
      ]);
      
      // Get supplements from latest assessment
      let recommendedSupps = [];
      if (assessRes.data.length > 0) {
        recommendedSupps = assessRes.data[0].recommended_supplements?.map(s => ({
          name: s.name,
          dosage: s.dosage,
          timing: s.timing
        })) || [];
      }
      setSupplements(recommendedSupps);
      
      // Load existing entry or initialize
      if (journalRes.data.entry) {
        const existingEntries = journalRes.data.entry.entries || [];
        // Merge with recommended supplements
        const mergedEntries = recommendedSupps.map(supp => {
          const existing = existingEntries.find(e => e.supplement_name === supp.name);
          return existing || {
            supplement_name: supp.name,
            taken: false,
            time_taken: '',
            dosage_taken: supp.dosage,
            notes: ''
          };
        });
        setEntries(mergedEntries);
        setOverallFeeling(journalRes.data.entry.overall_feeling || 5);
        setNotes(journalRes.data.entry.notes || '');
      } else {
        // Initialize new entries
        setEntries(recommendedSupps.map(supp => ({
          supplement_name: supp.name,
          taken: false,
          time_taken: '',
          dosage_taken: supp.dosage,
          notes: ''
        })));
        setOverallFeeling(5);
        setNotes('');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  }, [user, currentDate, getAuthHeader]);

  const fetchCorrelations = useCallback(async () => {
    if (!user) return;
    
    try {
      const res = await axios.get(`${API}/journal/correlation/analysis?days=30`, { 
        headers: getAuthHeader() 
      });
      setCorrelations(res.data);
    } catch (error) {
      console.error('Error fetching correlations:', error);
    }
  }, [user, getAuthHeader]);

  const fetchStats = useCallback(async () => {
    if (!user) return;
    
    try {
      const res = await axios.get(`${API}/journal/stats?days=30`, { 
        headers: getAuthHeader() 
      });
      setStats(res.data.stats);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [user, getAuthHeader]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (activeTab === 'insights') {
      fetchCorrelations();
    } else if (activeTab === 'stats') {
      fetchStats();
    }
  }, [activeTab, fetchCorrelations, fetchStats]);

  const toggleSupplementTaken = (index) => {
    setEntries(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        taken: !updated[index].taken,
        time_taken: !updated[index].taken ? new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : ''
      };
      return updated;
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await axios.post(`${API}/journal`, {
        date: currentDate,
        entries: entries,
        overall_feeling: overallFeeling,
        notes
      }, { headers: getAuthHeader() });
      
      toast.success('Journal saved!');
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const changeDate = (delta) => {
    const date = new Date(currentDate);
    date.setDate(date.getDate() + delta);
    if (date <= new Date()) {
      setCurrentDate(formatLocalDate(date));
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const today = todayLocal();
    const yesterday = daysAgoLocal(1);
    
    if (dateStr === today) return 'Today';
    if (dateStr === yesterday) return 'Yesterday';
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const getEffectivenessColor = (score) => {
    if (score >= 30) return 'text-green-600 bg-green-50';
    if (score >= 10) return 'text-emerald-600 bg-emerald-50';
    if (score >= -10) return 'text-gray-600 bg-gray-50';
    return 'text-red-600 bg-red-50';
  };

  const getRecommendationLabel = (rec) => {
    const labels = {
      'highly_effective': { text: 'Highly Effective', icon: Award, color: 'text-green-600' },
      'effective': { text: 'Effective', icon: TrendingUp, color: 'text-emerald-600' },
      'neutral': { text: 'Neutral', icon: Minus, color: 'text-gray-500' },
      'ineffective': { text: 'Review', icon: AlertCircle, color: 'text-amber-500' }
    };
    return labels[rec] || labels['neutral'];
  };

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  const takenCount = entries.filter(e => e.taken).length;

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-primary">
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </Link>
          <Logo size="default" linkTo="/" />
          <div className="w-20" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Title & Tabs */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-semibold text-[#1A1A1A] flex items-center gap-2">
              <BookOpen className="w-6 h-6 text-primary" />
              Supplement Journal
            </h1>
            <p className="text-muted-foreground">Track daily intake & discover what works</p>
          </div>
          <div className="flex gap-1 bg-muted p-1 rounded-lg">
            <button
              onClick={() => setActiveTab('log')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'log' ? 'bg-white shadow-sm text-primary' : 'text-muted-foreground'
              }`}
            >
              Log
            </button>
            <button
              onClick={() => setActiveTab('insights')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'insights' ? 'bg-white shadow-sm text-primary' : 'text-muted-foreground'
              }`}
            >
              Insights
            </button>
            <button
              onClick={() => setActiveTab('stats')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'stats' ? 'bg-white shadow-sm text-primary' : 'text-muted-foreground'
              }`}
            >
              Stats
            </button>
          </div>
        </div>

        <AnimatePresence mode="wait">
          {activeTab === 'log' && (
            <motion.div
              key="log"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              {/* Date Selector */}
              <div className="flex items-center justify-center gap-4 mb-8">
                <button
                  onClick={() => changeDate(-1)}
                  className="p-2 rounded-full hover:bg-muted transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg border border-border">
                  <Calendar className="w-4 h-4 text-primary" />
                  <span className="font-medium">{formatDate(currentDate)}</span>
                </div>
                <button
                  onClick={() => changeDate(1)}
                  disabled={currentDate === todayLocal()}
                  className="p-2 rounded-full hover:bg-muted transition-colors disabled:opacity-30"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>

              {supplements.length === 0 ? (
                <div className="bg-white rounded-2xl border border-border p-8 text-center">
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <Pill className="w-8 h-8 text-primary" />
                  </div>
                  <h2 className="text-xl font-semibold mb-2">Start Your Wellness Journey</h2>
                  <p className="text-muted-foreground mb-2">
                    Take a health assessment to get personalized supplement recommendations.
                  </p>
                  <p className="text-sm text-muted-foreground mb-6">
                    Once you have your protocol, you can track your daily intake here and discover patterns over time.
                  </p>
                  <Link to="/quiz">
                    <Button className="btn-primary">Take Free Assessment</Button>
                  </Link>
                </div>
              ) : (
                <>
                  {/* Progress Summary */}
                  <div className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-2xl border border-primary/20 p-4 mb-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center">
                          <span className="text-lg font-bold text-primary">
                            {takenCount}/{entries.length}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-[#1A1A1A]">Today's Progress</p>
                          <p className="text-sm text-muted-foreground">
                            {takenCount === entries.length 
                              ? 'All supplements taken!' 
                              : `${entries.length - takenCount} remaining`}
                          </p>
                        </div>
                      </div>
                      {takenCount === entries.length && (
                        <div className="flex items-center gap-1 text-green-600 bg-green-50 px-3 py-1 rounded-full">
                          <Sparkles className="w-4 h-4" />
                          <span className="text-sm font-medium">Complete!</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Supplement Checklist */}
                  <div className="bg-white rounded-2xl border border-border p-6 mb-6">
                    <h2 className="font-semibold text-lg mb-4">Today's Supplements</h2>
                    <div className="space-y-3">
                      {entries.map((entry, index) => {
                        const supp = supplements.find(s => s.name === entry.supplement_name);
                        return (
                          <button
                            key={entry.supplement_name}
                            onClick={() => toggleSupplementTaken(index)}
                            className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                              entry.taken
                                ? 'border-primary bg-primary/5'
                                : 'border-border bg-white hover:border-primary/30'
                            }`}
                          >
                            <div className="flex items-center gap-4">
                              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                                entry.taken 
                                  ? 'border-primary bg-primary' 
                                  : 'border-gray-300'
                              }`}>
                                {entry.taken && <Check className="w-4 h-4 text-white" />}
                              </div>
                              <div className="flex-1">
                                <p className={`font-medium ${entry.taken ? 'text-primary' : 'text-[#1A1A1A]'}`}>
                                  {entry.supplement_name}
                                </p>
                                <p className="text-sm text-muted-foreground">
                                  {supp?.dosage} • {supp?.timing}
                                </p>
                              </div>
                              {entry.taken && entry.time_taken && (
                                <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
                                  {entry.time_taken}
                                </span>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Overall Feeling */}
                  <div className="bg-white rounded-2xl border border-border p-6 mb-6">
                    <h2 className="font-semibold text-lg mb-4">How do you feel today?</h2>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm text-muted-foreground">Not great</span>
                      <span className="text-2xl font-bold text-primary">{overallFeeling}</span>
                      <span className="text-sm text-muted-foreground">Amazing</span>
                    </div>
                    <Slider
                      value={[overallFeeling]}
                      onValueChange={(val) => setOverallFeeling(val[0])}
                      min={1}
                      max={10}
                      step={1}
                      className="w-full"
                    />
                  </div>

                  {/* Notes */}
                  <div className="bg-white rounded-2xl border border-border p-6 mb-6">
                    <h2 className="font-semibold text-lg mb-4">Notes</h2>
                    <Textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Any observations, side effects, or things to remember..."
                      className="min-h-[80px] resize-none"
                    />
                  </div>

                  {/* Save Button */}
                  <Button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="w-full h-12 btn-primary"
                    data-testid="save-journal"
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Save Journal Entry
                      </>
                    )}
                  </Button>
                </>
              )}
            </motion.div>
          )}

          {activeTab === 'insights' && (
            <motion.div
              key="insights"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              {!correlations || correlations.correlations?.length === 0 ? (
                <div className="bg-white rounded-2xl border border-border p-8 text-center">
                  <BarChart3 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h2 className="text-xl font-semibold mb-2">Not enough data yet</h2>
                  <p className="text-muted-foreground mb-2">
                    {correlations?.message || 'Log your supplements for at least 3 days to see insights'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Current: {correlations?.total_days || 0} days logged
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Summary */}
                  {correlations.summary && (
                    <div className="bg-gradient-to-br from-primary/10 to-secondary/10 rounded-2xl border border-primary/20 p-6">
                      <h2 className="font-semibold text-lg mb-4 flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-primary" />
                        Your Insights
                      </h2>
                      <div className="space-y-3">
                        {correlations.summary.insights.slice(0, 4).map((insight, i) => (
                          <p key={i} className="text-sm text-[#1A1A1A] flex items-start gap-2">
                            <span className="text-primary mt-1">•</span>
                            {insight}
                          </p>
                        ))}
                      </div>
                      {correlations.summary.top_performers.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-primary/20">
                          <p className="text-sm font-medium text-primary">
                            Top performers: {correlations.summary.top_performers.join(', ')}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Correlation Cards */}
                  <div className="space-y-4">
                    <h2 className="font-semibold text-lg">Supplement Effectiveness</h2>
                    {correlations.correlations.map((corr) => {
                      const recLabel = getRecommendationLabel(corr.recommendation);
                      const RecIcon = recLabel.icon;
                      
                      return (
                        <div 
                          key={corr.supplement_name}
                          className="bg-white rounded-xl border border-border p-4"
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                <Pill className="w-5 h-5 text-primary" />
                              </div>
                              <div>
                                <p className="font-medium">{corr.supplement_name}</p>
                                <p className="text-xs text-muted-foreground">
                                  Taken {corr.days_taken} days
                                </p>
                              </div>
                            </div>
                            <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full ${getEffectivenessColor(corr.effectiveness_score)}`}>
                              <RecIcon className="w-4 h-4" />
                              <span className="text-sm font-medium">{recLabel.text}</span>
                            </div>
                          </div>
                          
                          {/* Feeling comparison */}
                          <div className="grid grid-cols-2 gap-4 mb-3">
                            <div className="text-center p-2 bg-muted/50 rounded-lg">
                              <p className="text-lg font-semibold text-green-600">
                                {corr.avg_feeling_when_taken}
                              </p>
                              <p className="text-xs text-muted-foreground">Feeling when taken</p>
                            </div>
                            <div className="text-center p-2 bg-muted/50 rounded-lg">
                              <p className="text-lg font-semibold text-gray-500">
                                {corr.avg_feeling_when_not_taken}
                              </p>
                              <p className="text-xs text-muted-foreground">Feeling when not taken</p>
                            </div>
                          </div>

                          {/* Symptom correlations */}
                          {Object.keys(corr.symptom_correlations).length > 0 && (
                            <div className="border-t border-border pt-3">
                              <p className="text-xs font-medium text-muted-foreground mb-2">
                                Symptom improvements:
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {Object.entries(corr.symptom_correlations)
                                  .filter(([_, data]) => data.improvement !== 0)
                                  .slice(0, 4)
                                  .map(([symptom, data]) => (
                                    <span 
                                      key={symptom}
                                      className={`text-xs px-2 py-1 rounded-full ${
                                        data.improvement > 0 
                                          ? 'bg-green-50 text-green-700' 
                                          : 'bg-red-50 text-red-700'
                                      }`}
                                    >
                                      {symptom.replace(/_/g, ' ')}: {data.improvement > 0 ? '+' : ''}{data.improvement}
                                    </span>
                                  ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {activeTab === 'stats' && (
            <motion.div
              key="stats"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              {!stats ? (
                <div className="bg-white rounded-2xl border border-border p-8 text-center">
                  <BarChart3 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h2 className="text-xl font-semibold mb-2">No stats yet</h2>
                  <p className="text-muted-foreground">
                    Start logging your supplements to see statistics
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-white rounded-xl border border-border p-4 text-center">
                      <p className="text-3xl font-bold text-primary">{stats.total_logged_days}</p>
                      <p className="text-sm text-muted-foreground">Days Logged</p>
                    </div>
                    <div className="bg-white rounded-xl border border-border p-4 text-center">
                      <p className="text-3xl font-bold text-primary">{stats.average_feeling}</p>
                      <p className="text-sm text-muted-foreground">Avg Feeling</p>
                    </div>
                    <div className="bg-white rounded-xl border border-border p-4 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Flame className="w-5 h-5 text-orange-500" />
                        <p className="text-3xl font-bold text-primary">{stats.current_streak}</p>
                      </div>
                      <p className="text-sm text-muted-foreground">Current Streak</p>
                    </div>
                    <div className="bg-white rounded-xl border border-border p-4 text-center">
                      <p className="text-3xl font-bold text-primary">{stats.completion_rate}%</p>
                      <p className="text-sm text-muted-foreground">Completion</p>
                    </div>
                  </div>

                  {/* Most Consistent */}
                  {stats.most_consistent_supplements.length > 0 && (
                    <div className="bg-white rounded-2xl border border-border p-6">
                      <h2 className="font-semibold text-lg mb-4 flex items-center gap-2">
                        <Award className="w-5 h-5 text-primary" />
                        Most Consistent
                      </h2>
                      <div className="space-y-3">
                        {stats.most_consistent_supplements.map((supp, i) => (
                          <div key={supp.name} className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-medium ${
                                i === 0 ? 'bg-amber-100 text-amber-700' :
                                i === 1 ? 'bg-gray-100 text-gray-700' :
                                i === 2 ? 'bg-orange-100 text-orange-700' :
                                'bg-muted text-muted-foreground'
                              }`}>
                                {i + 1}
                              </span>
                              <span className="font-medium">{supp.name}</span>
                            </div>
                            <span className="text-sm text-muted-foreground">
                              {supp.days} days
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Longest Streak */}
                  <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl border border-orange-200 p-6">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center">
                        <Flame className="w-6 h-6 text-orange-500" />
                      </div>
                      <div>
                        <p className="text-3xl font-bold text-orange-600">{stats.longest_streak} days</p>
                        <p className="text-sm text-orange-700">Longest streak ever</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
